// UI translations for the Suppliers module (13 languages).
// Keys use flat dotted names; {vars} are interpolated.
import { useT } from "@/lib/i18n";

export type SupplierDict = Record<string, string>;

const cs: SupplierDict = {
  "title": "Dodavatelé ({count})",
  "desc.prefix": "Seznam dodavatelů, od kterých nakupujete ingredience a nápoje. Přiřaďte každému dodavateli jednu nebo více kategorií. Odkazy lze vkládat pouze na",
  "desc.and": "a",
  "searchPh": "Hledat dodavatele…",
  "searchAria": "Hledat dodavatele",
  "categoryPh": "Kategorie",
  "allCategories": "Všechny kategorie",
  "sortAria": "Řazení",
  "sort.default": "Výchozí pořadí",
  "sort.name_asc": "Název (A–Z)",
  "sort.name_desc": "Název (Z–A)",
  "sort.updated_desc": "Naposledy upraveno (nejnovější)",
  "sort.updated_asc": "Naposledy upraveno (nejstarší)",
  "clearFilters": "Zrušit filtry",
  "showing.zero": "Zobrazeno 0 z {total}",
  "showing.range": "Zobrazeno {from}–{to} z {count}",
  "showing.filteredSuffix": " (filtrováno z {total})",
  "loading": "Načítám…",
  "empty": "Zatím žádný dodavatel.",
  "noMatch": "Žádný dodavatel neodpovídá zadaným filtrům.",
  "selectAllAria": "Vybrat všechny (odpovídající filtru)",
  "selected.n": "Vybráno {n}",
  "selected.mixedSuffix": " ({inFiltered} z {shown} zobrazených)",
  "selected.sameSuffix": " z {shown}",
  "selectAll": "Vybrat vše ({n})",
  "bulk.addCats": "Přidat kategorie",
  "bulk.removeCats": "Odebrat kategorie",
  "bulk.deleteConfirm": "Smazat {n} vybraných dodavatelů? Tuto akci nelze vrátit.",
  "bulk.deleteBtn": "Smazat vybrané",
  "bulk.clearSel": "Zrušit výběr",
  "row.selectAria": "Vybrat {name}",
  "row.edit": "Upravit",
  "row.delete": "Smazat",
  "row.deleteConfirm": "Smazat dodavatele „{name}\"?",
  "form.title.edit": "Upravit dodavatele",
  "form.title.add": "Přidat dodavatele",
  "form.name": "Název *",
  "form.namePh": "např. Řeznictví Novák",
  "form.categories": "Kategorie",
  "form.customCatPh": "Vlastní kategorie…",
  "form.add": "Přidat",
  "form.removeAria": "Odebrat {name}",
  "form.note": "Poznámka",
  "form.notePh": "volitelně: sortiment, region, spolupráce…",
  "form.allowedTitle": "Povolené domény pro odkazy dodavatele",
  "form.allowedSubs": "(a libovolné subdomény {subs})",
  "form.allowedProto": "Vyžadován protokol {https}, bez mezer, max. 500 znaků.",
  "form.link": "Odkaz na {label}",
  "form.onlyAllowed": "— povoleno pouze",
  "status.valid": "Validní",
  "status.invalid": "Nevalidní",
  "status.empty": "Prázdné",
  "err.expected": " Očekávána doména {host} nebo {subs}.",
  "err.useHttps": " Použijte {https}.",
  "err.fixLinks": "Opravte neplatné odkazy: {list}. Povolené domény: {hosts}.",
  "form.save": "Uložit změny",
  "form.cancel": "Zrušit",
  "form.tipFix": "Před uložením opravte neplatné odkazy.",
  "form.tipName": "Zadejte název dodavatele.",
  "bulk.selectedCount": "({n} vybraných)",
  "bulk.addDesc": "Vybrané kategorie se přidají ke všem označeným dodavatelům (duplicity se ignorují).",
  "bulk.removeDesc": "Vybrané kategorie se odstraní ze všech označených dodavatelů.",
  "pg.pageSize": "Na stránku",
  "pg.page": "Strana {cur} / {total}",
  // validation
  "v.tooLong": "Odkaz je příliš dlouhý (max 500 znaků).",
  "v.noSpaces": "Odkaz nesmí obsahovat mezery.",
  "v.invalidUrl": "Neplatná URL. Zadejte úplnou adresu začínající https:// (např. https://{host}/…).",
  "v.badProto": "Nepovolený protokol „{proto}\". Použijte https://.",
  "v.wrongHost": "Tento odkaz patří na {other}. Vložte ho do pole „Odkaz na {other}\".",
  "v.notAllowed": "Doména „{host}\" není povolena. Povoleny jsou pouze {hosts}.",
  "v.mustPoint": "Odkaz musí směřovat na {label}.",
  "v.nameReq": "Zadejte název dodavatele.",
  "v.nameLong": "Název je příliš dlouhý (max 120 znaků).",
  "v.noteLong": "Poznámka je příliš dlouhá (max 500 znaků).",
  "v.maxCats": "Maximálně 8 kategorií.",
  // toasts
  "t.editOk": "Dodavatel upraven.",
  "t.addOk": "Dodavatel přidán.",
  "t.delOk": "Dodavatel smazán.",
  "t.bulkDelOk": "Smazáno {n} dodavatelů.",
  "t.bulkEditOk": "Upraveno {n} dodavatelů.",
  "t.linkNorm": "Odkaz upraven do standardního tvaru.",
  // bulk import
  "imp.btn": "Hromadný import",
  "imp.title": "Hromadný import dodavatelů",
  "imp.desc": "Vložte data z Excelu/Google Sheets nebo nahrajte CSV/XLSX soubor. Povinný sloupec: {name}. Volitelné: {list}.",
  "imp.uploadFile": "Nahrát soubor (CSV/XLSX)",
  "imp.downloadTpl": "Stáhnout šablonu CSV",
  "imp.pasteLabel": "Nebo vložte CSV / TSV text",
  "imp.headerErr.empty": "Prázdný soubor.",
  "imp.headerErr.noName": "Chybí sloupec „name\" (nebo „název\").",
  "imp.validCnt": "Připraveno: {n}",
  "imp.invalidCnt": "S chybou: {n}",
  "imp.totalRows": "Celkem řádků: {n}",
  "imp.th.row": "Řádek",
  "imp.th.name": "Název",
  "imp.th.categories": "Kategorie",
  "imp.th.links": "Odkazy",
  "imp.th.status": "Stav",
  "imp.rowEmpty": "(prázdné)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Známé kategorie: {list}. Vlastní kategorie jsou povoleny (max 8 na dodavatele).",
  "imp.cancel": "Zrušit",
  "imp.importBtn": "Importovat",
  "imp.err.rowNoName": "Chybí název",
  "imp.err.rowNameLong": "Název delší než 120 znaků",
  "imp.err.invalidUrl": "Neplatná URL ({label})",
  "imp.err.badProto": "Nepovolený protokol ({label})",
  "imp.err.wrongHost": "Odkaz patří na {other}, ne na {label}",
  "imp.err.notAllowed": "Doména „{host}\" není povolena ({label})",
  "imp.err.readFile": "Nepodařilo se načíst soubor: {msg}",
  "imp.err.nothing": "Není co importovat.",
  "imp.toast.imported": "Importováno dodavatelů: {n}",
  "imp.toast.failed": "Import selhal: {msg}",
  // public section
  "pub.header": "Dodavatelé",
  "pub.all": "Vše",
  // supplier detail page
  "det.head.title": "Dodavatel — Cerveza.International",
  "det.head.desc": "Detail dodavatele s odkazy na jeho externí profily.",
  "det.err.title": "Chyba",
  "det.err.retry": "Zkusit znovu",
  "det.nf.title": "Dodavatel nenalezen",
  "det.nf.back": "← Zpět na profil podniku",
  "det.forBusiness": "Dodavatel pro {name}",
  "det.cats.title": "Kategorie dodávek",
  "det.note.title": "Poznámka",
  "det.ext.title": "Externí profily",
  "det.ext.empty": "Dodavatel zatím neuvedl žádný externí profil.",
  "det.ext.open": "Otevřít",
  "det.allowed": "Povolené domény: {list}.",
  "det.businessFallback": "podnik",
};

const sk: SupplierDict = {
  "title": "Dodávatelia ({count})",
  "desc.prefix": "Zoznam dodávateľov, od ktorých nakupujete suroviny a nápoje. Priraďte každému dodávateľovi jednu alebo viac kategórií. Odkazy je možné vkladať iba na",
  "desc.and": "a",
  "searchPh": "Hľadať dodávateľa…",
  "searchAria": "Hľadať dodávateľa",
  "categoryPh": "Kategória",
  "allCategories": "Všetky kategórie",
  "sortAria": "Zoradenie",
  "sort.default": "Predvolené poradie",
  "sort.name_asc": "Názov (A–Z)",
  "sort.name_desc": "Názov (Z–A)",
  "sort.updated_desc": "Naposledy upravené (najnovšie)",
  "sort.updated_asc": "Naposledy upravené (najstaršie)",
  "clearFilters": "Zrušiť filtre",
  "showing.zero": "Zobrazené 0 z {total}",
  "showing.range": "Zobrazené {from}–{to} z {count}",
  "showing.filteredSuffix": " (filtrované z {total})",
  "loading": "Načítavam…",
  "empty": "Zatiaľ žiadny dodávateľ.",
  "noMatch": "Žiadny dodávateľ nezodpovedá zadaným filtrom.",
  "selectAllAria": "Vybrať všetky (podľa filtra)",
  "selected.n": "Vybraných {n}",
  "selected.mixedSuffix": " ({inFiltered} z {shown} zobrazených)",
  "selected.sameSuffix": " z {shown}",
  "selectAll": "Vybrať všetko ({n})",
  "bulk.addCats": "Pridať kategórie",
  "bulk.removeCats": "Odobrať kategórie",
  "bulk.deleteConfirm": "Zmazať {n} vybraných dodávateľov? Túto akciu nemožno vrátiť.",
  "bulk.deleteBtn": "Zmazať vybrané",
  "bulk.clearSel": "Zrušiť výber",
  "row.selectAria": "Vybrať {name}",
  "row.edit": "Upraviť",
  "row.delete": "Zmazať",
  "row.deleteConfirm": "Zmazať dodávateľa „{name}\"?",
  "form.title.edit": "Upraviť dodávateľa",
  "form.title.add": "Pridať dodávateľa",
  "form.name": "Názov *",
  "form.namePh": "napr. Mäsiarstvo Novák",
  "form.categories": "Kategórie",
  "form.customCatPh": "Vlastná kategória…",
  "form.add": "Pridať",
  "form.removeAria": "Odobrať {name}",
  "form.note": "Poznámka",
  "form.notePh": "voliteľne: sortiment, región, spolupráca…",
  "form.allowedTitle": "Povolené domény pre odkazy dodávateľa",
  "form.allowedSubs": "(a ľubovoľné subdomény {subs})",
  "form.allowedProto": "Vyžaduje sa protokol {https}, bez medzier, max. 500 znakov.",
  "form.link": "Odkaz na {label}",
  "form.onlyAllowed": "— povolené iba",
  "status.valid": "Platný",
  "status.invalid": "Neplatný",
  "status.empty": "Prázdne",
  "err.expected": " Očakáva sa doména {host} alebo {subs}.",
  "err.useHttps": " Použite {https}.",
  "err.fixLinks": "Opravte neplatné odkazy: {list}. Povolené domény: {hosts}.",
  "form.save": "Uložiť zmeny",
  "form.cancel": "Zrušiť",
  "form.tipFix": "Pred uložením opravte neplatné odkazy.",
  "form.tipName": "Zadajte názov dodávateľa.",
  "bulk.selectedCount": "({n} vybraných)",
  "bulk.addDesc": "Vybrané kategórie sa pridajú ku všetkým označeným dodávateľom (duplicity sa ignorujú).",
  "bulk.removeDesc": "Vybrané kategórie sa odstránia zo všetkých označených dodávateľov.",
  "pg.pageSize": "Na stránku",
  "pg.page": "Strana {cur} / {total}",
  "v.tooLong": "Odkaz je príliš dlhý (max 500 znakov).",
  "v.noSpaces": "Odkaz nesmie obsahovať medzery.",
  "v.invalidUrl": "Neplatná URL. Zadajte úplnú adresu začínajúcu https:// (napr. https://{host}/…).",
  "v.badProto": "Nepovolený protokol „{proto}\". Použite https://.",
  "v.wrongHost": "Tento odkaz patrí na {other}. Vložte ho do poľa „Odkaz na {other}\".",
  "v.notAllowed": "Doména „{host}\" nie je povolená. Povolené sú iba {hosts}.",
  "v.mustPoint": "Odkaz musí smerovať na {label}.",
  "v.nameReq": "Zadajte názov dodávateľa.",
  "v.nameLong": "Názov je príliš dlhý (max 120 znakov).",
  "v.noteLong": "Poznámka je príliš dlhá (max 500 znakov).",
  "v.maxCats": "Maximálne 8 kategórií.",
  "t.editOk": "Dodávateľ upravený.",
  "t.addOk": "Dodávateľ pridaný.",
  "t.delOk": "Dodávateľ zmazaný.",
  "t.bulkDelOk": "Zmazaných {n} dodávateľov.",
  "t.bulkEditOk": "Upravených {n} dodávateľov.",
  "t.linkNorm": "Odkaz upravený do štandardného tvaru.",
  "imp.btn": "Hromadný import",
  "imp.title": "Hromadný import dodávateľov",
  "imp.desc": "Vložte údaje z Excelu/Google Sheets alebo nahrajte súbor CSV/XLSX. Povinný stĺpec: {name}. Voliteľné: {list}.",
  "imp.uploadFile": "Nahrať súbor (CSV/XLSX)",
  "imp.downloadTpl": "Stiahnuť šablónu CSV",
  "imp.pasteLabel": "Alebo vložte CSV / TSV text",
  "imp.headerErr.empty": "Prázdny súbor.",
  "imp.headerErr.noName": "Chýba stĺpec „name\" (alebo „názov\").",
  "imp.validCnt": "Pripravené: {n}",
  "imp.invalidCnt": "S chybou: {n}",
  "imp.totalRows": "Spolu riadkov: {n}",
  "imp.th.row": "Riadok",
  "imp.th.name": "Názov",
  "imp.th.categories": "Kategórie",
  "imp.th.links": "Odkazy",
  "imp.th.status": "Stav",
  "imp.rowEmpty": "(prázdne)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Známe kategórie: {list}. Vlastné kategórie sú povolené (max 8 na dodávateľa).",
  "imp.cancel": "Zrušiť",
  "imp.importBtn": "Importovať",
  "imp.err.rowNoName": "Chýba názov",
  "imp.err.rowNameLong": "Názov dlhší ako 120 znakov",
  "imp.err.invalidUrl": "Neplatná URL ({label})",
  "imp.err.badProto": "Nepovolený protokol ({label})",
  "imp.err.wrongHost": "Odkaz patrí na {other}, nie na {label}",
  "imp.err.notAllowed": "Doména „{host}\" nie je povolená ({label})",
  "imp.err.readFile": "Nepodarilo sa načítať súbor: {msg}",
  "imp.err.nothing": "Nie je čo importovať.",
  "imp.toast.imported": "Importovaných dodávateľov: {n}",
  "imp.toast.failed": "Import zlyhal: {msg}",
  "pub.header": "Dodávatelia",
  "pub.all": "Všetko",
  "det.head.title": "Dodávateľ — Cerveza.International",
  "det.head.desc": "Detail dodávateľa s odkazmi na jeho externé profily.",
  "det.err.title": "Chyba",
  "det.err.retry": "Skúsiť znova",
  "det.nf.title": "Dodávateľ nenájdený",
  "det.nf.back": "← Späť na profil podniku",
  "det.forBusiness": "Dodávateľ pre {name}",
  "det.cats.title": "Kategórie dodávok",
  "det.note.title": "Poznámka",
  "det.ext.title": "Externé profily",
  "det.ext.empty": "Dodávateľ zatiaľ neuviedol žiadny externý profil.",
  "det.ext.open": "Otvoriť",
  "det.allowed": "Povolené domény: {list}.",
  "det.businessFallback": "podnik",
};

const pl: SupplierDict = {
  "title": "Dostawcy ({count})",
  "desc.prefix": "Lista dostawców, od których kupujesz składniki i napoje. Przypisz każdemu dostawcy jedną lub więcej kategorii. Linki można wstawiać wyłącznie na",
  "desc.and": "i",
  "searchPh": "Szukaj dostawcy…",
  "searchAria": "Szukaj dostawcy",
  "categoryPh": "Kategoria",
  "allCategories": "Wszystkie kategorie",
  "sortAria": "Sortowanie",
  "sort.default": "Kolejność domyślna",
  "sort.name_asc": "Nazwa (A–Z)",
  "sort.name_desc": "Nazwa (Z–A)",
  "sort.updated_desc": "Ostatnio zmienione (najnowsze)",
  "sort.updated_asc": "Ostatnio zmienione (najstarsze)",
  "clearFilters": "Wyczyść filtry",
  "showing.zero": "Wyświetlono 0 z {total}",
  "showing.range": "Wyświetlono {from}–{to} z {count}",
  "showing.filteredSuffix": " (filtrowane z {total})",
  "loading": "Ładowanie…",
  "empty": "Brak dostawców.",
  "noMatch": "Żaden dostawca nie pasuje do filtrów.",
  "selectAllAria": "Zaznacz wszystkie (zgodne z filtrem)",
  "selected.n": "Zaznaczono {n}",
  "selected.mixedSuffix": " ({inFiltered} z {shown} widocznych)",
  "selected.sameSuffix": " z {shown}",
  "selectAll": "Zaznacz wszystkie ({n})",
  "bulk.addCats": "Dodaj kategorie",
  "bulk.removeCats": "Usuń kategorie",
  "bulk.deleteConfirm": "Usunąć {n} zaznaczonych dostawców? Tej operacji nie można cofnąć.",
  "bulk.deleteBtn": "Usuń zaznaczone",
  "bulk.clearSel": "Wyczyść zaznaczenie",
  "row.selectAria": "Zaznacz {name}",
  "row.edit": "Edytuj",
  "row.delete": "Usuń",
  "row.deleteConfirm": "Usunąć dostawcę „{name}\"?",
  "form.title.edit": "Edytuj dostawcę",
  "form.title.add": "Dodaj dostawcę",
  "form.name": "Nazwa *",
  "form.namePh": "np. Rzeźnictwo Nowak",
  "form.categories": "Kategorie",
  "form.customCatPh": "Własna kategoria…",
  "form.add": "Dodaj",
  "form.removeAria": "Usuń {name}",
  "form.note": "Notatka",
  "form.notePh": "opcjonalnie: asortyment, region, współpraca…",
  "form.allowedTitle": "Dozwolone domeny dla linków dostawcy",
  "form.allowedSubs": "(oraz dowolne subdomeny {subs})",
  "form.allowedProto": "Wymagany protokół {https}, bez spacji, maks. 500 znaków.",
  "form.link": "Link do {label}",
  "form.onlyAllowed": "— dozwolone tylko",
  "status.valid": "Poprawny",
  "status.invalid": "Niepoprawny",
  "status.empty": "Pusty",
  "err.expected": " Oczekiwana domena {host} lub {subs}.",
  "err.useHttps": " Użyj {https}.",
  "err.fixLinks": "Popraw niepoprawne linki: {list}. Dozwolone domeny: {hosts}.",
  "form.save": "Zapisz zmiany",
  "form.cancel": "Anuluj",
  "form.tipFix": "Przed zapisem popraw niepoprawne linki.",
  "form.tipName": "Podaj nazwę dostawcy.",
  "bulk.selectedCount": "({n} zaznaczonych)",
  "bulk.addDesc": "Wybrane kategorie zostaną dodane do wszystkich zaznaczonych dostawców (duplikaty pominięte).",
  "bulk.removeDesc": "Wybrane kategorie zostaną usunięte ze wszystkich zaznaczonych dostawców.",
  "pg.pageSize": "Na stronę",
  "pg.page": "Strona {cur} / {total}",
  "v.tooLong": "Link jest za długi (maks 500 znaków).",
  "v.noSpaces": "Link nie może zawierać spacji.",
  "v.invalidUrl": "Nieprawidłowy URL. Wpisz pełny adres zaczynający się od https:// (np. https://{host}/…).",
  "v.badProto": "Niedozwolony protokół „{proto}\". Użyj https://.",
  "v.wrongHost": "Ten link należy do {other}. Wstaw go w polu „Link do {other}\".",
  "v.notAllowed": "Domena „{host}\" jest niedozwolona. Dozwolone są tylko {hosts}.",
  "v.mustPoint": "Link musi prowadzić do {label}.",
  "v.nameReq": "Podaj nazwę dostawcy.",
  "v.nameLong": "Nazwa jest za długa (maks 120 znaków).",
  "v.noteLong": "Notatka jest za długa (maks 500 znaków).",
  "v.maxCats": "Maksymalnie 8 kategorii.",
  "t.editOk": "Dostawca zaktualizowany.",
  "t.addOk": "Dostawca dodany.",
  "t.delOk": "Dostawca usunięty.",
  "t.bulkDelOk": "Usunięto {n} dostawców.",
  "t.bulkEditOk": "Zaktualizowano {n} dostawców.",
  "t.linkNorm": "Link zapisany w standardowej postaci.",
  "imp.btn": "Import zbiorczy",
  "imp.title": "Import zbiorczy dostawców",
  "imp.desc": "Wklej dane z Excela/Google Sheets lub wczytaj plik CSV/XLSX. Wymagana kolumna: {name}. Opcjonalne: {list}.",
  "imp.uploadFile": "Wczytaj plik (CSV/XLSX)",
  "imp.downloadTpl": "Pobierz szablon CSV",
  "imp.pasteLabel": "Lub wklej tekst CSV / TSV",
  "imp.headerErr.empty": "Pusty plik.",
  "imp.headerErr.noName": "Brak kolumny „name\" (lub „nazwa\").",
  "imp.validCnt": "Gotowe: {n}",
  "imp.invalidCnt": "Z błędem: {n}",
  "imp.totalRows": "Wierszy łącznie: {n}",
  "imp.th.row": "Wiersz",
  "imp.th.name": "Nazwa",
  "imp.th.categories": "Kategorie",
  "imp.th.links": "Linki",
  "imp.th.status": "Status",
  "imp.rowEmpty": "(puste)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Znane kategorie: {list}. Własne kategorie są dozwolone (maks 8 na dostawcę).",
  "imp.cancel": "Anuluj",
  "imp.importBtn": "Importuj",
  "imp.err.rowNoName": "Brak nazwy",
  "imp.err.rowNameLong": "Nazwa dłuższa niż 120 znaków",
  "imp.err.invalidUrl": "Nieprawidłowy URL ({label})",
  "imp.err.badProto": "Niedozwolony protokół ({label})",
  "imp.err.wrongHost": "Link należy do {other}, a nie do {label}",
  "imp.err.notAllowed": "Domena „{host}\" jest niedozwolona ({label})",
  "imp.err.readFile": "Nie udało się wczytać pliku: {msg}",
  "imp.err.nothing": "Nic do zaimportowania.",
  "imp.toast.imported": "Zaimportowano dostawców: {n}",
  "imp.toast.failed": "Import nieudany: {msg}",
  "pub.header": "Dostawcy",
  "pub.all": "Wszystko",
  "det.head.title": "Dostawca — Cerveza.International",
  "det.head.desc": "Szczegóły dostawcy z linkami do jego zewnętrznych profili.",
  "det.err.title": "Błąd",
  "det.err.retry": "Spróbuj ponownie",
  "det.nf.title": "Nie znaleziono dostawcy",
  "det.nf.back": "← Powrót do profilu lokalu",
  "det.forBusiness": "Dostawca dla {name}",
  "det.cats.title": "Kategorie dostaw",
  "det.note.title": "Notatka",
  "det.ext.title": "Profile zewnętrzne",
  "det.ext.empty": "Dostawca nie podał jeszcze żadnego profilu zewnętrznego.",
  "det.ext.open": "Otwórz",
  "det.allowed": "Dozwolone domeny: {list}.",
  "det.businessFallback": "lokal",
};

const en: SupplierDict = {
  "title": "Suppliers ({count})",
  "desc.prefix": "List of suppliers you buy ingredients and drinks from. Assign one or more categories to each supplier. Links may only point to",
  "desc.and": "and",
  "searchPh": "Search suppliers…",
  "searchAria": "Search suppliers",
  "categoryPh": "Category",
  "allCategories": "All categories",
  "sortAria": "Sort",
  "sort.default": "Default order",
  "sort.name_asc": "Name (A–Z)",
  "sort.name_desc": "Name (Z–A)",
  "sort.updated_desc": "Recently updated (newest)",
  "sort.updated_asc": "Recently updated (oldest)",
  "clearFilters": "Clear filters",
  "showing.zero": "Showing 0 of {total}",
  "showing.range": "Showing {from}–{to} of {count}",
  "showing.filteredSuffix": " (filtered from {total})",
  "loading": "Loading…",
  "empty": "No suppliers yet.",
  "noMatch": "No supplier matches the filters.",
  "selectAllAria": "Select all (matching filter)",
  "selected.n": "{n} selected",
  "selected.mixedSuffix": " ({inFiltered} of {shown} visible)",
  "selected.sameSuffix": " of {shown}",
  "selectAll": "Select all ({n})",
  "bulk.addCats": "Add categories",
  "bulk.removeCats": "Remove categories",
  "bulk.deleteConfirm": "Delete {n} selected suppliers? This cannot be undone.",
  "bulk.deleteBtn": "Delete selected",
  "bulk.clearSel": "Clear selection",
  "row.selectAria": "Select {name}",
  "row.edit": "Edit",
  "row.delete": "Delete",
  "row.deleteConfirm": "Delete supplier \"{name}\"?",
  "form.title.edit": "Edit supplier",
  "form.title.add": "Add supplier",
  "form.name": "Name *",
  "form.namePh": "e.g. Butcher Novák",
  "form.categories": "Categories",
  "form.customCatPh": "Custom category…",
  "form.add": "Add",
  "form.removeAria": "Remove {name}",
  "form.note": "Note",
  "form.notePh": "optional: assortment, region, partnership…",
  "form.allowedTitle": "Allowed domains for supplier links",
  "form.allowedSubs": "(and any subdomains {subs})",
  "form.allowedProto": "Requires {https} protocol, no spaces, max 500 characters.",
  "form.link": "Link to {label}",
  "form.onlyAllowed": "— only",
  "status.valid": "Valid",
  "status.invalid": "Invalid",
  "status.empty": "Empty",
  "err.expected": " Expected domain {host} or {subs}.",
  "err.useHttps": " Use {https}.",
  "err.fixLinks": "Fix invalid links: {list}. Allowed domains: {hosts}.",
  "form.save": "Save changes",
  "form.cancel": "Cancel",
  "form.tipFix": "Fix invalid links before saving.",
  "form.tipName": "Enter the supplier name.",
  "bulk.selectedCount": "({n} selected)",
  "bulk.addDesc": "Selected categories will be added to all marked suppliers (duplicates ignored).",
  "bulk.removeDesc": "Selected categories will be removed from all marked suppliers.",
  "pg.pageSize": "Per page",
  "pg.page": "Page {cur} / {total}",
  "v.tooLong": "Link is too long (max 500 characters).",
  "v.noSpaces": "Link cannot contain spaces.",
  "v.invalidUrl": "Invalid URL. Enter a full address starting with https:// (e.g. https://{host}/…).",
  "v.badProto": "Protocol \"{proto}\" is not allowed. Use https://.",
  "v.wrongHost": "This link belongs to {other}. Put it in the \"Link to {other}\" field.",
  "v.notAllowed": "Domain \"{host}\" is not allowed. Only {hosts} are allowed.",
  "v.mustPoint": "Link must point to {label}.",
  "v.nameReq": "Enter the supplier name.",
  "v.nameLong": "Name is too long (max 120 characters).",
  "v.noteLong": "Note is too long (max 500 characters).",
  "v.maxCats": "Maximum 8 categories.",
  "t.editOk": "Supplier updated.",
  "t.addOk": "Supplier added.",
  "t.delOk": "Supplier deleted.",
  "t.bulkDelOk": "Deleted {n} suppliers.",
  "t.bulkEditOk": "Updated {n} suppliers.",
  "t.linkNorm": "Link normalized to standard form.",
  "imp.btn": "Bulk import",
  "imp.title": "Bulk import of suppliers",
  "imp.desc": "Paste data from Excel/Google Sheets or upload a CSV/XLSX file. Required column: {name}. Optional: {list}.",
  "imp.uploadFile": "Upload file (CSV/XLSX)",
  "imp.downloadTpl": "Download CSV template",
  "imp.pasteLabel": "Or paste CSV / TSV text",
  "imp.headerErr.empty": "Empty file.",
  "imp.headerErr.noName": "Missing column \"name\" (or \"název\").",
  "imp.validCnt": "Ready: {n}",
  "imp.invalidCnt": "With error: {n}",
  "imp.totalRows": "Total rows: {n}",
  "imp.th.row": "Row",
  "imp.th.name": "Name",
  "imp.th.categories": "Categories",
  "imp.th.links": "Links",
  "imp.th.status": "Status",
  "imp.rowEmpty": "(empty)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Known categories: {list}. Custom categories are allowed (max 8 per supplier).",
  "imp.cancel": "Cancel",
  "imp.importBtn": "Import",
  "imp.err.rowNoName": "Missing name",
  "imp.err.rowNameLong": "Name longer than 120 characters",
  "imp.err.invalidUrl": "Invalid URL ({label})",
  "imp.err.badProto": "Protocol not allowed ({label})",
  "imp.err.wrongHost": "Link belongs to {other}, not {label}",
  "imp.err.notAllowed": "Domain \"{host}\" is not allowed ({label})",
  "imp.err.readFile": "Failed to read file: {msg}",
  "imp.err.nothing": "Nothing to import.",
  "imp.toast.imported": "Suppliers imported: {n}",
  "imp.toast.failed": "Import failed: {msg}",
  "pub.header": "Suppliers",
  "pub.all": "All",
  "det.head.title": "Supplier — Cerveza.International",
  "det.head.desc": "Supplier details with links to its external profiles.",
  "det.err.title": "Error",
  "det.err.retry": "Try again",
  "det.nf.title": "Supplier not found",
  "det.nf.back": "← Back to business profile",
  "det.forBusiness": "Supplier for {name}",
  "det.cats.title": "Supply categories",
  "det.note.title": "Note",
  "det.ext.title": "External profiles",
  "det.ext.empty": "The supplier has not provided any external profile yet.",
  "det.ext.open": "Open",
  "det.allowed": "Allowed domains: {list}.",
  "det.businessFallback": "business",
};

const es: SupplierDict = {
  "title": "Proveedores ({count})",
  "desc.prefix": "Lista de proveedores a los que compras ingredientes y bebidas. Asigna una o varias categorías a cada proveedor. Los enlaces solo pueden apuntar a",
  "desc.and": "y",
  "searchPh": "Buscar proveedor…",
  "searchAria": "Buscar proveedor",
  "categoryPh": "Categoría",
  "allCategories": "Todas las categorías",
  "sortAria": "Ordenar",
  "sort.default": "Orden predeterminado",
  "sort.name_asc": "Nombre (A–Z)",
  "sort.name_desc": "Nombre (Z–A)",
  "sort.updated_desc": "Actualizado recientemente (nuevos)",
  "sort.updated_asc": "Actualizado recientemente (antiguos)",
  "clearFilters": "Borrar filtros",
  "showing.zero": "Mostrando 0 de {total}",
  "showing.range": "Mostrando {from}–{to} de {count}",
  "showing.filteredSuffix": " (filtrado de {total})",
  "loading": "Cargando…",
  "empty": "Aún no hay proveedores.",
  "noMatch": "Ningún proveedor coincide con los filtros.",
  "selectAllAria": "Seleccionar todos (según filtro)",
  "selected.n": "{n} seleccionados",
  "selected.mixedSuffix": " ({inFiltered} de {shown} visibles)",
  "selected.sameSuffix": " de {shown}",
  "selectAll": "Seleccionar todo ({n})",
  "bulk.addCats": "Añadir categorías",
  "bulk.removeCats": "Quitar categorías",
  "bulk.deleteConfirm": "¿Eliminar {n} proveedores seleccionados? Esta acción no se puede deshacer.",
  "bulk.deleteBtn": "Eliminar seleccionados",
  "bulk.clearSel": "Anular selección",
  "row.selectAria": "Seleccionar {name}",
  "row.edit": "Editar",
  "row.delete": "Eliminar",
  "row.deleteConfirm": "¿Eliminar el proveedor «{name}»?",
  "form.title.edit": "Editar proveedor",
  "form.title.add": "Añadir proveedor",
  "form.name": "Nombre *",
  "form.namePh": "p. ej. Carnicería Novák",
  "form.categories": "Categorías",
  "form.customCatPh": "Categoría personalizada…",
  "form.add": "Añadir",
  "form.removeAria": "Quitar {name}",
  "form.note": "Nota",
  "form.notePh": "opcional: surtido, región, colaboración…",
  "form.allowedTitle": "Dominios permitidos para los enlaces del proveedor",
  "form.allowedSubs": "(y cualquier subdominio {subs})",
  "form.allowedProto": "Se requiere protocolo {https}, sin espacios, máx. 500 caracteres.",
  "form.link": "Enlace a {label}",
  "form.onlyAllowed": "— solo se permite",
  "status.valid": "Válido",
  "status.invalid": "No válido",
  "status.empty": "Vacío",
  "err.expected": " Se esperaba el dominio {host} o {subs}.",
  "err.useHttps": " Usa {https}.",
  "err.fixLinks": "Corrige los enlaces no válidos: {list}. Dominios permitidos: {hosts}.",
  "form.save": "Guardar cambios",
  "form.cancel": "Cancelar",
  "form.tipFix": "Corrige los enlaces no válidos antes de guardar.",
  "form.tipName": "Introduce el nombre del proveedor.",
  "bulk.selectedCount": "({n} seleccionados)",
  "bulk.addDesc": "Las categorías seleccionadas se añadirán a todos los proveedores marcados (se ignoran duplicados).",
  "bulk.removeDesc": "Las categorías seleccionadas se quitarán de todos los proveedores marcados.",
  "pg.pageSize": "Por página",
  "pg.page": "Página {cur} / {total}",
  "v.tooLong": "El enlace es demasiado largo (máx. 500 caracteres).",
  "v.noSpaces": "El enlace no puede contener espacios.",
  "v.invalidUrl": "URL no válida. Introduce una dirección completa que empiece por https:// (p. ej. https://{host}/…).",
  "v.badProto": "Protocolo «{proto}» no permitido. Usa https://.",
  "v.wrongHost": "Este enlace corresponde a {other}. Ponlo en el campo «Enlace a {other}».",
  "v.notAllowed": "El dominio «{host}» no está permitido. Solo se permiten {hosts}.",
  "v.mustPoint": "El enlace debe apuntar a {label}.",
  "v.nameReq": "Introduce el nombre del proveedor.",
  "v.nameLong": "El nombre es demasiado largo (máx. 120 caracteres).",
  "v.noteLong": "La nota es demasiado larga (máx. 500 caracteres).",
  "v.maxCats": "Máximo 8 categorías.",
  "t.editOk": "Proveedor actualizado.",
  "t.addOk": "Proveedor añadido.",
  "t.delOk": "Proveedor eliminado.",
  "t.bulkDelOk": "{n} proveedores eliminados.",
  "t.bulkEditOk": "{n} proveedores actualizados.",
  "t.linkNorm": "Enlace normalizado al formato estándar.",
  "imp.btn": "Importación masiva",
  "imp.title": "Importación masiva de proveedores",
  "imp.desc": "Pega datos de Excel/Google Sheets o sube un archivo CSV/XLSX. Columna obligatoria: {name}. Opcionales: {list}.",
  "imp.uploadFile": "Subir archivo (CSV/XLSX)",
  "imp.downloadTpl": "Descargar plantilla CSV",
  "imp.pasteLabel": "O pega texto CSV / TSV",
  "imp.headerErr.empty": "Archivo vacío.",
  "imp.headerErr.noName": "Falta la columna «name» (o «název»).",
  "imp.validCnt": "Listo: {n}",
  "imp.invalidCnt": "Con error: {n}",
  "imp.totalRows": "Filas totales: {n}",
  "imp.th.row": "Fila",
  "imp.th.name": "Nombre",
  "imp.th.categories": "Categorías",
  "imp.th.links": "Enlaces",
  "imp.th.status": "Estado",
  "imp.rowEmpty": "(vacío)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Categorías conocidas: {list}. Se permiten categorías propias (máx. 8 por proveedor).",
  "imp.cancel": "Cancelar",
  "imp.importBtn": "Importar",
  "imp.err.rowNoName": "Falta el nombre",
  "imp.err.rowNameLong": "Nombre superior a 120 caracteres",
  "imp.err.invalidUrl": "URL no válida ({label})",
  "imp.err.badProto": "Protocolo no permitido ({label})",
  "imp.err.wrongHost": "El enlace corresponde a {other}, no a {label}",
  "imp.err.notAllowed": "El dominio «{host}» no está permitido ({label})",
  "imp.err.readFile": "No se pudo leer el archivo: {msg}",
  "imp.err.nothing": "Nada que importar.",
  "imp.toast.imported": "Proveedores importados: {n}",
  "imp.toast.failed": "Importación fallida: {msg}",
  "pub.header": "Proveedores",
  "pub.all": "Todo",
  "det.head.title": "Proveedor — Cerveza.International",
  "det.head.desc": "Detalle del proveedor con enlaces a sus perfiles externos.",
  "det.err.title": "Error",
  "det.err.retry": "Reintentar",
  "det.nf.title": "Proveedor no encontrado",
  "det.nf.back": "← Volver al perfil del local",
  "det.forBusiness": "Proveedor para {name}",
  "det.cats.title": "Categorías de suministro",
  "det.note.title": "Nota",
  "det.ext.title": "Perfiles externos",
  "det.ext.empty": "El proveedor aún no ha indicado ningún perfil externo.",
  "det.ext.open": "Abrir",
  "det.allowed": "Dominios permitidos: {list}.",
  "det.businessFallback": "local",
};

const de: SupplierDict = {
  "title": "Lieferanten ({count})",
  "desc.prefix": "Liste der Lieferanten, bei denen Sie Zutaten und Getränke einkaufen. Weisen Sie jedem Lieferanten eine oder mehrere Kategorien zu. Links dürfen ausschließlich auf",
  "desc.and": "und",
  "searchPh": "Lieferanten suchen…",
  "searchAria": "Lieferanten suchen",
  "categoryPh": "Kategorie",
  "allCategories": "Alle Kategorien",
  "sortAria": "Sortierung",
  "sort.default": "Standardreihenfolge",
  "sort.name_asc": "Name (A–Z)",
  "sort.name_desc": "Name (Z–A)",
  "sort.updated_desc": "Zuletzt bearbeitet (neueste)",
  "sort.updated_asc": "Zuletzt bearbeitet (älteste)",
  "clearFilters": "Filter zurücksetzen",
  "showing.zero": "0 von {total} angezeigt",
  "showing.range": "{from}–{to} von {count} angezeigt",
  "showing.filteredSuffix": " (gefiltert aus {total})",
  "loading": "Wird geladen…",
  "empty": "Noch keine Lieferanten.",
  "noMatch": "Kein Lieferant entspricht den Filtern.",
  "selectAllAria": "Alle auswählen (nach Filter)",
  "selected.n": "{n} ausgewählt",
  "selected.mixedSuffix": " ({inFiltered} von {shown} sichtbar)",
  "selected.sameSuffix": " von {shown}",
  "selectAll": "Alle auswählen ({n})",
  "bulk.addCats": "Kategorien hinzufügen",
  "bulk.removeCats": "Kategorien entfernen",
  "bulk.deleteConfirm": "{n} ausgewählte Lieferanten löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
  "bulk.deleteBtn": "Ausgewählte löschen",
  "bulk.clearSel": "Auswahl aufheben",
  "row.selectAria": "{name} auswählen",
  "row.edit": "Bearbeiten",
  "row.delete": "Löschen",
  "row.deleteConfirm": "Lieferant „{name}\" löschen?",
  "form.title.edit": "Lieferant bearbeiten",
  "form.title.add": "Lieferant hinzufügen",
  "form.name": "Name *",
  "form.namePh": "z. B. Metzgerei Novák",
  "form.categories": "Kategorien",
  "form.customCatPh": "Eigene Kategorie…",
  "form.add": "Hinzufügen",
  "form.removeAria": "{name} entfernen",
  "form.note": "Notiz",
  "form.notePh": "optional: Sortiment, Region, Zusammenarbeit…",
  "form.allowedTitle": "Erlaubte Domains für Lieferanten-Links",
  "form.allowedSubs": "(und beliebige Subdomains {subs})",
  "form.allowedProto": "Protokoll {https} erforderlich, keine Leerzeichen, max. 500 Zeichen.",
  "form.link": "Link zu {label}",
  "form.onlyAllowed": "— nur",
  "status.valid": "Gültig",
  "status.invalid": "Ungültig",
  "status.empty": "Leer",
  "err.expected": " Erwartet wird Domain {host} oder {subs}.",
  "err.useHttps": " Verwenden Sie {https}.",
  "err.fixLinks": "Ungültige Links korrigieren: {list}. Erlaubte Domains: {hosts}.",
  "form.save": "Änderungen speichern",
  "form.cancel": "Abbrechen",
  "form.tipFix": "Vor dem Speichern ungültige Links korrigieren.",
  "form.tipName": "Namen des Lieferanten eingeben.",
  "bulk.selectedCount": "({n} ausgewählt)",
  "bulk.addDesc": "Die ausgewählten Kategorien werden allen markierten Lieferanten hinzugefügt (Duplikate werden ignoriert).",
  "bulk.removeDesc": "Die ausgewählten Kategorien werden von allen markierten Lieferanten entfernt.",
  "pg.pageSize": "Pro Seite",
  "pg.page": "Seite {cur} / {total}",
  "v.tooLong": "Link ist zu lang (max. 500 Zeichen).",
  "v.noSpaces": "Link darf keine Leerzeichen enthalten.",
  "v.invalidUrl": "Ungültige URL. Geben Sie eine vollständige Adresse mit https:// ein (z. B. https://{host}/…).",
  "v.badProto": "Protokoll „{proto}\" ist nicht erlaubt. Verwenden Sie https://.",
  "v.wrongHost": "Dieser Link gehört zu {other}. Fügen Sie ihn im Feld „Link zu {other}\" ein.",
  "v.notAllowed": "Domain „{host}\" ist nicht erlaubt. Erlaubt sind nur {hosts}.",
  "v.mustPoint": "Link muss auf {label} verweisen.",
  "v.nameReq": "Namen des Lieferanten eingeben.",
  "v.nameLong": "Name ist zu lang (max. 120 Zeichen).",
  "v.noteLong": "Notiz ist zu lang (max. 500 Zeichen).",
  "v.maxCats": "Maximal 8 Kategorien.",
  "t.editOk": "Lieferant aktualisiert.",
  "t.addOk": "Lieferant hinzugefügt.",
  "t.delOk": "Lieferant gelöscht.",
  "t.bulkDelOk": "{n} Lieferanten gelöscht.",
  "t.bulkEditOk": "{n} Lieferanten aktualisiert.",
  "t.linkNorm": "Link auf Standardform normalisiert.",
  "imp.btn": "Massenimport",
  "imp.title": "Massenimport von Lieferanten",
  "imp.desc": "Daten aus Excel/Google Sheets einfügen oder CSV/XLSX-Datei hochladen. Pflichtspalte: {name}. Optional: {list}.",
  "imp.uploadFile": "Datei hochladen (CSV/XLSX)",
  "imp.downloadTpl": "CSV-Vorlage herunterladen",
  "imp.pasteLabel": "Oder CSV/TSV-Text einfügen",
  "imp.headerErr.empty": "Leere Datei.",
  "imp.headerErr.noName": "Spalte „name\" (oder „název\") fehlt.",
  "imp.validCnt": "Bereit: {n}",
  "imp.invalidCnt": "Mit Fehler: {n}",
  "imp.totalRows": "Zeilen gesamt: {n}",
  "imp.th.row": "Zeile",
  "imp.th.name": "Name",
  "imp.th.categories": "Kategorien",
  "imp.th.links": "Links",
  "imp.th.status": "Status",
  "imp.rowEmpty": "(leer)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Bekannte Kategorien: {list}. Eigene Kategorien sind erlaubt (max. 8 pro Lieferant).",
  "imp.cancel": "Abbrechen",
  "imp.importBtn": "Importieren",
  "imp.err.rowNoName": "Name fehlt",
  "imp.err.rowNameLong": "Name länger als 120 Zeichen",
  "imp.err.invalidUrl": "Ungültige URL ({label})",
  "imp.err.badProto": "Protokoll nicht erlaubt ({label})",
  "imp.err.wrongHost": "Link gehört zu {other}, nicht zu {label}",
  "imp.err.notAllowed": "Domain „{host}\" ist nicht erlaubt ({label})",
  "imp.err.readFile": "Datei konnte nicht gelesen werden: {msg}",
  "imp.err.nothing": "Nichts zu importieren.",
  "imp.toast.imported": "Importierte Lieferanten: {n}",
  "imp.toast.failed": "Import fehlgeschlagen: {msg}",
  "pub.header": "Lieferanten",
  "pub.all": "Alle",
  "det.head.title": "Lieferant — Cerveza.International",
  "det.head.desc": "Lieferantendetails mit Links zu externen Profilen.",
  "det.err.title": "Fehler",
  "det.err.retry": "Erneut versuchen",
  "det.nf.title": "Lieferant nicht gefunden",
  "det.nf.back": "← Zurück zum Profil",
  "det.forBusiness": "Lieferant für {name}",
  "det.cats.title": "Lieferkategorien",
  "det.note.title": "Notiz",
  "det.ext.title": "Externe Profile",
  "det.ext.empty": "Der Lieferant hat noch keine externen Profile angegeben.",
  "det.ext.open": "Öffnen",
  "det.allowed": "Erlaubte Domains: {list}.",
  "det.businessFallback": "Betrieb",
};

const pt: SupplierDict = {
  "title": "Fornecedores ({count})",
  "desc.prefix": "Lista de fornecedores a quem compra ingredientes e bebidas. Atribua uma ou mais categorias a cada fornecedor. Os links só podem apontar para",
  "desc.and": "e",
  "searchPh": "Procurar fornecedor…",
  "searchAria": "Procurar fornecedor",
  "categoryPh": "Categoria",
  "allCategories": "Todas as categorias",
  "sortAria": "Ordenar",
  "sort.default": "Ordem predefinida",
  "sort.name_asc": "Nome (A–Z)",
  "sort.name_desc": "Nome (Z–A)",
  "sort.updated_desc": "Atualizado recentemente (mais recentes)",
  "sort.updated_asc": "Atualizado recentemente (mais antigos)",
  "clearFilters": "Limpar filtros",
  "showing.zero": "A mostrar 0 de {total}",
  "showing.range": "A mostrar {from}–{to} de {count}",
  "showing.filteredSuffix": " (filtrado de {total})",
  "loading": "A carregar…",
  "empty": "Ainda sem fornecedores.",
  "noMatch": "Nenhum fornecedor corresponde aos filtros.",
  "selectAllAria": "Selecionar todos (segundo o filtro)",
  "selected.n": "{n} selecionados",
  "selected.mixedSuffix": " ({inFiltered} de {shown} visíveis)",
  "selected.sameSuffix": " de {shown}",
  "selectAll": "Selecionar tudo ({n})",
  "bulk.addCats": "Adicionar categorias",
  "bulk.removeCats": "Remover categorias",
  "bulk.deleteConfirm": "Eliminar {n} fornecedores selecionados? Esta ação não pode ser anulada.",
  "bulk.deleteBtn": "Eliminar selecionados",
  "bulk.clearSel": "Anular seleção",
  "row.selectAria": "Selecionar {name}",
  "row.edit": "Editar",
  "row.delete": "Eliminar",
  "row.deleteConfirm": "Eliminar o fornecedor «{name}»?",
  "form.title.edit": "Editar fornecedor",
  "form.title.add": "Adicionar fornecedor",
  "form.name": "Nome *",
  "form.namePh": "p. ex. Talho Novák",
  "form.categories": "Categorias",
  "form.customCatPh": "Categoria personalizada…",
  "form.add": "Adicionar",
  "form.removeAria": "Remover {name}",
  "form.note": "Nota",
  "form.notePh": "opcional: sortido, região, colaboração…",
  "form.allowedTitle": "Domínios permitidos para os links do fornecedor",
  "form.allowedSubs": "(e qualquer subdomínio {subs})",
  "form.allowedProto": "Requer protocolo {https}, sem espaços, máx. 500 carateres.",
  "form.link": "Link para {label}",
  "form.onlyAllowed": "— apenas",
  "status.valid": "Válido",
  "status.invalid": "Inválido",
  "status.empty": "Vazio",
  "err.expected": " Esperado o domínio {host} ou {subs}.",
  "err.useHttps": " Utilize {https}.",
  "err.fixLinks": "Corrija os links inválidos: {list}. Domínios permitidos: {hosts}.",
  "form.save": "Guardar alterações",
  "form.cancel": "Cancelar",
  "form.tipFix": "Corrija os links inválidos antes de guardar.",
  "form.tipName": "Introduza o nome do fornecedor.",
  "bulk.selectedCount": "({n} selecionados)",
  "bulk.addDesc": "As categorias selecionadas serão adicionadas a todos os fornecedores marcados (duplicados ignorados).",
  "bulk.removeDesc": "As categorias selecionadas serão removidas de todos os fornecedores marcados.",
  "pg.pageSize": "Por página",
  "pg.page": "Página {cur} / {total}",
  "v.tooLong": "O link é demasiado longo (máx. 500 carateres).",
  "v.noSpaces": "O link não pode conter espaços.",
  "v.invalidUrl": "URL inválido. Introduza o endereço completo começando por https:// (p. ex. https://{host}/…).",
  "v.badProto": "Protocolo «{proto}» não permitido. Utilize https://.",
  "v.wrongHost": "Este link pertence a {other}. Coloque-o no campo «Link para {other}».",
  "v.notAllowed": "O domínio «{host}» não é permitido. Só são permitidos {hosts}.",
  "v.mustPoint": "O link deve apontar para {label}.",
  "v.nameReq": "Introduza o nome do fornecedor.",
  "v.nameLong": "Nome demasiado longo (máx. 120 carateres).",
  "v.noteLong": "Nota demasiado longa (máx. 500 carateres).",
  "v.maxCats": "No máximo 8 categorias.",
  "t.editOk": "Fornecedor atualizado.",
  "t.addOk": "Fornecedor adicionado.",
  "t.delOk": "Fornecedor eliminado.",
  "t.bulkDelOk": "{n} fornecedores eliminados.",
  "t.bulkEditOk": "{n} fornecedores atualizados.",
  "t.linkNorm": "Link normalizado para o formato padrão.",
  "imp.btn": "Importação em massa",
  "imp.title": "Importação em massa de fornecedores",
  "imp.desc": "Cole dados do Excel/Google Sheets ou carregue um ficheiro CSV/XLSX. Coluna obrigatória: {name}. Opcionais: {list}.",
  "imp.uploadFile": "Carregar ficheiro (CSV/XLSX)",
  "imp.downloadTpl": "Descarregar modelo CSV",
  "imp.pasteLabel": "Ou cole texto CSV / TSV",
  "imp.headerErr.empty": "Ficheiro vazio.",
  "imp.headerErr.noName": "Falta a coluna «name» (ou «název»).",
  "imp.validCnt": "Pronto: {n}",
  "imp.invalidCnt": "Com erro: {n}",
  "imp.totalRows": "Total de linhas: {n}",
  "imp.th.row": "Linha",
  "imp.th.name": "Nome",
  "imp.th.categories": "Categorias",
  "imp.th.links": "Links",
  "imp.th.status": "Estado",
  "imp.rowEmpty": "(vazio)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Categorias conhecidas: {list}. Categorias próprias são permitidas (máx. 8 por fornecedor).",
  "imp.cancel": "Cancelar",
  "imp.importBtn": "Importar",
  "imp.err.rowNoName": "Falta o nome",
  "imp.err.rowNameLong": "Nome com mais de 120 carateres",
  "imp.err.invalidUrl": "URL inválido ({label})",
  "imp.err.badProto": "Protocolo não permitido ({label})",
  "imp.err.wrongHost": "O link pertence a {other}, não a {label}",
  "imp.err.notAllowed": "Domínio «{host}» não permitido ({label})",
  "imp.err.readFile": "Não foi possível ler o ficheiro: {msg}",
  "imp.err.nothing": "Nada para importar.",
  "imp.toast.imported": "Fornecedores importados: {n}",
  "imp.toast.failed": "Importação falhou: {msg}",
  "pub.header": "Fornecedores",
  "pub.all": "Tudo",
  "det.head.title": "Fornecedor — Cerveza.International",
  "det.head.desc": "Detalhe do fornecedor com links para os seus perfis externos.",
  "det.err.title": "Erro",
  "det.err.retry": "Tentar novamente",
  "det.nf.title": "Fornecedor não encontrado",
  "det.nf.back": "← Voltar ao perfil do estabelecimento",
  "det.forBusiness": "Fornecedor para {name}",
  "det.cats.title": "Categorias de fornecimento",
  "det.note.title": "Nota",
  "det.ext.title": "Perfis externos",
  "det.ext.empty": "O fornecedor ainda não indicou nenhum perfil externo.",
  "det.ext.open": "Abrir",
  "det.allowed": "Domínios permitidos: {list}.",
  "det.businessFallback": "estabelecimento",
};

const fr: SupplierDict = {
  "title": "Fournisseurs ({count})",
  "desc.prefix": "Liste des fournisseurs auprès desquels vous achetez ingrédients et boissons. Attribuez une ou plusieurs catégories à chaque fournisseur. Les liens ne peuvent pointer que vers",
  "desc.and": "et",
  "searchPh": "Rechercher un fournisseur…",
  "searchAria": "Rechercher un fournisseur",
  "categoryPh": "Catégorie",
  "allCategories": "Toutes les catégories",
  "sortAria": "Tri",
  "sort.default": "Ordre par défaut",
  "sort.name_asc": "Nom (A–Z)",
  "sort.name_desc": "Nom (Z–A)",
  "sort.updated_desc": "Modifié récemment (le plus récent)",
  "sort.updated_asc": "Modifié récemment (le plus ancien)",
  "clearFilters": "Effacer les filtres",
  "showing.zero": "0 sur {total} affiché(s)",
  "showing.range": "{from}–{to} sur {count} affichés",
  "showing.filteredSuffix": " (filtré depuis {total})",
  "loading": "Chargement…",
  "empty": "Aucun fournisseur pour l'instant.",
  "noMatch": "Aucun fournisseur ne correspond aux filtres.",
  "selectAllAria": "Tout sélectionner (selon filtre)",
  "selected.n": "{n} sélectionné(s)",
  "selected.mixedSuffix": " ({inFiltered} sur {shown} visibles)",
  "selected.sameSuffix": " sur {shown}",
  "selectAll": "Tout sélectionner ({n})",
  "bulk.addCats": "Ajouter des catégories",
  "bulk.removeCats": "Retirer des catégories",
  "bulk.deleteConfirm": "Supprimer {n} fournisseurs sélectionnés ? Action irréversible.",
  "bulk.deleteBtn": "Supprimer la sélection",
  "bulk.clearSel": "Annuler la sélection",
  "row.selectAria": "Sélectionner {name}",
  "row.edit": "Modifier",
  "row.delete": "Supprimer",
  "row.deleteConfirm": "Supprimer le fournisseur « {name} » ?",
  "form.title.edit": "Modifier le fournisseur",
  "form.title.add": "Ajouter un fournisseur",
  "form.name": "Nom *",
  "form.namePh": "p. ex. Boucherie Novák",
  "form.categories": "Catégories",
  "form.customCatPh": "Catégorie personnalisée…",
  "form.add": "Ajouter",
  "form.removeAria": "Retirer {name}",
  "form.note": "Note",
  "form.notePh": "facultatif : gamme, région, partenariat…",
  "form.allowedTitle": "Domaines autorisés pour les liens du fournisseur",
  "form.allowedSubs": "(et tout sous-domaine {subs})",
  "form.allowedProto": "Protocole {https} requis, sans espaces, 500 caractères max.",
  "form.link": "Lien vers {label}",
  "form.onlyAllowed": "— autorisé uniquement",
  "status.valid": "Valide",
  "status.invalid": "Non valide",
  "status.empty": "Vide",
  "err.expected": " Domaine attendu {host} ou {subs}.",
  "err.useHttps": " Utilisez {https}.",
  "err.fixLinks": "Corrigez les liens non valides : {list}. Domaines autorisés : {hosts}.",
  "form.save": "Enregistrer",
  "form.cancel": "Annuler",
  "form.tipFix": "Corrigez les liens non valides avant d'enregistrer.",
  "form.tipName": "Saisissez le nom du fournisseur.",
  "bulk.selectedCount": "({n} sélectionnés)",
  "bulk.addDesc": "Les catégories sélectionnées seront ajoutées à tous les fournisseurs cochés (les doublons sont ignorés).",
  "bulk.removeDesc": "Les catégories sélectionnées seront retirées de tous les fournisseurs cochés.",
  "pg.pageSize": "Par page",
  "pg.page": "Page {cur} / {total}",
  "v.tooLong": "Lien trop long (500 caractères max).",
  "v.noSpaces": "Le lien ne peut pas contenir d'espaces.",
  "v.invalidUrl": "URL non valide. Saisissez l'adresse complète commençant par https:// (p. ex. https://{host}/…).",
  "v.badProto": "Protocole « {proto} » non autorisé. Utilisez https://.",
  "v.wrongHost": "Ce lien concerne {other}. Placez-le dans le champ « Lien vers {other} ».",
  "v.notAllowed": "Le domaine « {host} » n'est pas autorisé. Seuls {hosts} sont autorisés.",
  "v.mustPoint": "Le lien doit pointer vers {label}.",
  "v.nameReq": "Saisissez le nom du fournisseur.",
  "v.nameLong": "Nom trop long (120 caractères max).",
  "v.noteLong": "Note trop longue (500 caractères max).",
  "v.maxCats": "8 catégories maximum.",
  "t.editOk": "Fournisseur mis à jour.",
  "t.addOk": "Fournisseur ajouté.",
  "t.delOk": "Fournisseur supprimé.",
  "t.bulkDelOk": "{n} fournisseurs supprimés.",
  "t.bulkEditOk": "{n} fournisseurs mis à jour.",
  "t.linkNorm": "Lien normalisé au format standard.",
  "imp.btn": "Import en masse",
  "imp.title": "Import en masse de fournisseurs",
  "imp.desc": "Collez les données d'Excel/Google Sheets ou déposez un fichier CSV/XLSX. Colonne requise : {name}. Facultatif : {list}.",
  "imp.uploadFile": "Envoyer un fichier (CSV/XLSX)",
  "imp.downloadTpl": "Télécharger le modèle CSV",
  "imp.pasteLabel": "Ou collez du texte CSV / TSV",
  "imp.headerErr.empty": "Fichier vide.",
  "imp.headerErr.noName": "Colonne « name » (ou « nom ») manquante.",
  "imp.validCnt": "Prêt : {n}",
  "imp.invalidCnt": "En erreur : {n}",
  "imp.totalRows": "Lignes totales : {n}",
  "imp.th.row": "Ligne",
  "imp.th.name": "Nom",
  "imp.th.categories": "Catégories",
  "imp.th.links": "Liens",
  "imp.th.status": "Statut",
  "imp.rowEmpty": "(vide)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Catégories connues : {list}. Les catégories personnalisées sont autorisées (8 max par fournisseur).",
  "imp.cancel": "Annuler",
  "imp.importBtn": "Importer",
  "imp.err.rowNoName": "Nom manquant",
  "imp.err.rowNameLong": "Nom de plus de 120 caractères",
  "imp.err.invalidUrl": "URL non valide ({label})",
  "imp.err.badProto": "Protocole non autorisé ({label})",
  "imp.err.wrongHost": "Le lien concerne {other}, pas {label}",
  "imp.err.notAllowed": "Domaine « {host} » non autorisé ({label})",
  "imp.err.readFile": "Impossible de lire le fichier : {msg}",
  "imp.err.nothing": "Rien à importer.",
  "imp.toast.imported": "Fournisseurs importés : {n}",
  "imp.toast.failed": "Import échoué : {msg}",
  "pub.header": "Fournisseurs",
  "pub.all": "Tous",
  "det.head.title": "Fournisseur — Cerveza.International",
  "det.head.desc": "Fiche du fournisseur avec ses profils externes.",
  "det.err.title": "Erreur",
  "det.err.retry": "Réessayer",
  "det.nf.title": "Fournisseur introuvable",
  "det.nf.back": "← Retour au profil de l'établissement",
  "det.forBusiness": "Fournisseur pour {name}",
  "det.cats.title": "Catégories de fourniture",
  "det.note.title": "Note",
  "det.ext.title": "Profils externes",
  "det.ext.empty": "Le fournisseur n'a pas encore indiqué de profil externe.",
  "det.ext.open": "Ouvrir",
  "det.allowed": "Domaines autorisés : {list}.",
  "det.businessFallback": "établissement",
};

const hr: SupplierDict = {
  "title": "Dobavljači ({count})",
  "desc.prefix": "Popis dobavljača od kojih kupujete namirnice i pića. Svakom dobavljaču dodijelite jednu ili više kategorija. Poveznice mogu voditi samo na",
  "desc.and": "i",
  "searchPh": "Traži dobavljača…",
  "searchAria": "Traži dobavljača",
  "categoryPh": "Kategorija",
  "allCategories": "Sve kategorije",
  "sortAria": "Sortiranje",
  "sort.default": "Zadani redoslijed",
  "sort.name_asc": "Naziv (A–Z)",
  "sort.name_desc": "Naziv (Z–A)",
  "sort.updated_desc": "Nedavno ažurirano (najnovije)",
  "sort.updated_asc": "Nedavno ažurirano (najstarije)",
  "clearFilters": "Očisti filtre",
  "showing.zero": "Prikazano 0 od {total}",
  "showing.range": "Prikazano {from}–{to} od {count}",
  "showing.filteredSuffix": " (filtrirano iz {total})",
  "loading": "Učitavanje…",
  "empty": "Zasad nema dobavljača.",
  "noMatch": "Nijedan dobavljač ne odgovara filtrima.",
  "selectAllAria": "Odaberi sve (prema filtru)",
  "selected.n": "Odabrano {n}",
  "selected.mixedSuffix": " ({inFiltered} od {shown} prikazanih)",
  "selected.sameSuffix": " od {shown}",
  "selectAll": "Odaberi sve ({n})",
  "bulk.addCats": "Dodaj kategorije",
  "bulk.removeCats": "Ukloni kategorije",
  "bulk.deleteConfirm": "Izbrisati {n} odabranih dobavljača? Radnja je nepovratna.",
  "bulk.deleteBtn": "Izbriši odabrane",
  "bulk.clearSel": "Očisti odabir",
  "row.selectAria": "Odaberi {name}",
  "row.edit": "Uredi",
  "row.delete": "Izbriši",
  "row.deleteConfirm": "Izbrisati dobavljača „{name}\"?",
  "form.title.edit": "Uredi dobavljača",
  "form.title.add": "Dodaj dobavljača",
  "form.name": "Naziv *",
  "form.namePh": "npr. Mesnica Novák",
  "form.categories": "Kategorije",
  "form.customCatPh": "Vlastita kategorija…",
  "form.add": "Dodaj",
  "form.removeAria": "Ukloni {name}",
  "form.note": "Napomena",
  "form.notePh": "neobavezno: asortiman, regija, suradnja…",
  "form.allowedTitle": "Dozvoljene domene za poveznice dobavljača",
  "form.allowedSubs": "(i bilo koje poddomene {subs})",
  "form.allowedProto": "Potreban protokol {https}, bez razmaka, najviše 500 znakova.",
  "form.link": "Poveznica na {label}",
  "form.onlyAllowed": "— dopušteno samo",
  "status.valid": "Valjano",
  "status.invalid": "Nevažeće",
  "status.empty": "Prazno",
  "err.expected": " Očekivana domena {host} ili {subs}.",
  "err.useHttps": " Koristite {https}.",
  "err.fixLinks": "Ispravite nevažeće poveznice: {list}. Dopuštene domene: {hosts}.",
  "form.save": "Spremi promjene",
  "form.cancel": "Odustani",
  "form.tipFix": "Prije spremanja ispravite nevažeće poveznice.",
  "form.tipName": "Unesite naziv dobavljača.",
  "bulk.selectedCount": "({n} odabranih)",
  "bulk.addDesc": "Odabrane kategorije dodat će se svim označenim dobavljačima (duplikati se ignoriraju).",
  "bulk.removeDesc": "Odabrane kategorije uklonit će se sa svih označenih dobavljača.",
  "pg.pageSize": "Po stranici",
  "pg.page": "Stranica {cur} / {total}",
  "v.tooLong": "Poveznica je predugačka (maks. 500 znakova).",
  "v.noSpaces": "Poveznica ne smije sadržavati razmake.",
  "v.invalidUrl": "Nevažeći URL. Unesite potpunu adresu koja počinje s https:// (npr. https://{host}/…).",
  "v.badProto": "Protokol „{proto}\" nije dopušten. Koristite https://.",
  "v.wrongHost": "Ova poveznica pripada {other}. Stavite je u polje „Poveznica na {other}\".",
  "v.notAllowed": "Domena „{host}\" nije dopuštena. Dopuštene su samo {hosts}.",
  "v.mustPoint": "Poveznica mora voditi na {label}.",
  "v.nameReq": "Unesite naziv dobavljača.",
  "v.nameLong": "Naziv je predugačak (maks. 120 znakova).",
  "v.noteLong": "Napomena je predugačka (maks. 500 znakova).",
  "v.maxCats": "Najviše 8 kategorija.",
  "t.editOk": "Dobavljač ažuriran.",
  "t.addOk": "Dobavljač dodan.",
  "t.delOk": "Dobavljač izbrisan.",
  "t.bulkDelOk": "Izbrisano {n} dobavljača.",
  "t.bulkEditOk": "Ažurirano {n} dobavljača.",
  "t.linkNorm": "Poveznica normalizirana u standardni oblik.",
  "imp.btn": "Skupni uvoz",
  "imp.title": "Skupni uvoz dobavljača",
  "imp.desc": "Zalijepite podatke iz Excela/Google Sheetsa ili učitajte CSV/XLSX datoteku. Obavezan stupac: {name}. Neobavezno: {list}.",
  "imp.uploadFile": "Učitaj datoteku (CSV/XLSX)",
  "imp.downloadTpl": "Preuzmi CSV predložak",
  "imp.pasteLabel": "Ili zalijepite CSV / TSV tekst",
  "imp.headerErr.empty": "Prazna datoteka.",
  "imp.headerErr.noName": "Nedostaje stupac „name\" (ili „naziv\").",
  "imp.validCnt": "Spremno: {n}",
  "imp.invalidCnt": "S greškom: {n}",
  "imp.totalRows": "Ukupno redaka: {n}",
  "imp.th.row": "Redak",
  "imp.th.name": "Naziv",
  "imp.th.categories": "Kategorije",
  "imp.th.links": "Poveznice",
  "imp.th.status": "Status",
  "imp.rowEmpty": "(prazno)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Poznate kategorije: {list}. Vlastite kategorije su dopuštene (maks. 8 po dobavljaču).",
  "imp.cancel": "Odustani",
  "imp.importBtn": "Uvezi",
  "imp.err.rowNoName": "Nedostaje naziv",
  "imp.err.rowNameLong": "Naziv dulji od 120 znakova",
  "imp.err.invalidUrl": "Nevažeći URL ({label})",
  "imp.err.badProto": "Protokol nije dopušten ({label})",
  "imp.err.wrongHost": "Poveznica pripada {other}, ne {label}",
  "imp.err.notAllowed": "Domena „{host}\" nije dopuštena ({label})",
  "imp.err.readFile": "Datoteku nije moguće učitati: {msg}",
  "imp.err.nothing": "Nema što za uvesti.",
  "imp.toast.imported": "Uvezeno dobavljača: {n}",
  "imp.toast.failed": "Uvoz neuspješan: {msg}",
  "pub.header": "Dobavljači",
  "pub.all": "Sve",
  "det.head.title": "Dobavljač — Cerveza.International",
  "det.head.desc": "Detalji dobavljača s poveznicama na njegove vanjske profile.",
  "det.err.title": "Greška",
  "det.err.retry": "Pokušaj ponovno",
  "det.nf.title": "Dobavljač nije pronađen",
  "det.nf.back": "← Natrag na profil objekta",
  "det.forBusiness": "Dobavljač za {name}",
  "det.cats.title": "Kategorije dobave",
  "det.note.title": "Napomena",
  "det.ext.title": "Vanjski profili",
  "det.ext.empty": "Dobavljač još nije naveo nijedan vanjski profil.",
  "det.ext.open": "Otvori",
  "det.allowed": "Dopuštene domene: {list}.",
  "det.businessFallback": "objekt",
};

const ro: SupplierDict = {
  "title": "Furnizori ({count})",
  "desc.prefix": "Lista furnizorilor de la care cumpărați ingrediente și băuturi. Atribuiți una sau mai multe categorii fiecărui furnizor. Linkurile pot indica doar către",
  "desc.and": "și",
  "searchPh": "Caută furnizor…",
  "searchAria": "Caută furnizor",
  "categoryPh": "Categorie",
  "allCategories": "Toate categoriile",
  "sortAria": "Sortare",
  "sort.default": "Ordine implicită",
  "sort.name_asc": "Nume (A–Z)",
  "sort.name_desc": "Nume (Z–A)",
  "sort.updated_desc": "Actualizat recent (cele mai noi)",
  "sort.updated_asc": "Actualizat recent (cele mai vechi)",
  "clearFilters": "Șterge filtrele",
  "showing.zero": "Se afișează 0 din {total}",
  "showing.range": "Se afișează {from}–{to} din {count}",
  "showing.filteredSuffix": " (filtrat din {total})",
  "loading": "Se încarcă…",
  "empty": "Încă niciun furnizor.",
  "noMatch": "Niciun furnizor nu se potrivește filtrelor.",
  "selectAllAria": "Selectează tot (după filtru)",
  "selected.n": "{n} selectate",
  "selected.mixedSuffix": " ({inFiltered} din {shown} vizibile)",
  "selected.sameSuffix": " din {shown}",
  "selectAll": "Selectează tot ({n})",
  "bulk.addCats": "Adaugă categorii",
  "bulk.removeCats": "Elimină categorii",
  "bulk.deleteConfirm": "Ștergeți {n} furnizori selectați? Acțiune ireversibilă.",
  "bulk.deleteBtn": "Șterge selecția",
  "bulk.clearSel": "Anulează selecția",
  "row.selectAria": "Selectează {name}",
  "row.edit": "Editează",
  "row.delete": "Șterge",
  "row.deleteConfirm": "Ștergeți furnizorul „{name}\"?",
  "form.title.edit": "Editează furnizorul",
  "form.title.add": "Adaugă furnizor",
  "form.name": "Nume *",
  "form.namePh": "ex.: Măcelăria Novák",
  "form.categories": "Categorii",
  "form.customCatPh": "Categorie proprie…",
  "form.add": "Adaugă",
  "form.removeAria": "Elimină {name}",
  "form.note": "Notă",
  "form.notePh": "opțional: sortiment, regiune, colaborare…",
  "form.allowedTitle": "Domenii permise pentru linkurile furnizorului",
  "form.allowedSubs": "(și orice subdomeniu {subs})",
  "form.allowedProto": "Necesită protocolul {https}, fără spații, max. 500 de caractere.",
  "form.link": "Link către {label}",
  "form.onlyAllowed": "— permis doar",
  "status.valid": "Valid",
  "status.invalid": "Nevalid",
  "status.empty": "Gol",
  "err.expected": " Se aștepta domeniul {host} sau {subs}.",
  "err.useHttps": " Folosiți {https}.",
  "err.fixLinks": "Corectați linkurile nevalide: {list}. Domenii permise: {hosts}.",
  "form.save": "Salvează modificările",
  "form.cancel": "Anulează",
  "form.tipFix": "Corectați linkurile nevalide înainte de a salva.",
  "form.tipName": "Introduceți numele furnizorului.",
  "bulk.selectedCount": "({n} selectate)",
  "bulk.addDesc": "Categoriile selectate vor fi adăugate tuturor furnizorilor marcați (duplicatele sunt ignorate).",
  "bulk.removeDesc": "Categoriile selectate vor fi eliminate de la toți furnizorii marcați.",
  "pg.pageSize": "Pe pagină",
  "pg.page": "Pagina {cur} / {total}",
  "v.tooLong": "Linkul este prea lung (max. 500 de caractere).",
  "v.noSpaces": "Linkul nu poate conține spații.",
  "v.invalidUrl": "URL nevalid. Introduceți adresa completă începând cu https:// (ex.: https://{host}/…).",
  "v.badProto": "Protocolul „{proto}\" nu este permis. Folosiți https://.",
  "v.wrongHost": "Acest link aparține {other}. Puneți-l în câmpul „Link către {other}\".",
  "v.notAllowed": "Domeniul „{host}\" nu este permis. Sunt permise doar {hosts}.",
  "v.mustPoint": "Linkul trebuie să indice către {label}.",
  "v.nameReq": "Introduceți numele furnizorului.",
  "v.nameLong": "Nume prea lung (max. 120 de caractere).",
  "v.noteLong": "Nota este prea lungă (max. 500 de caractere).",
  "v.maxCats": "Maximum 8 categorii.",
  "t.editOk": "Furnizor actualizat.",
  "t.addOk": "Furnizor adăugat.",
  "t.delOk": "Furnizor șters.",
  "t.bulkDelOk": "{n} furnizori șterși.",
  "t.bulkEditOk": "{n} furnizori actualizați.",
  "t.linkNorm": "Link normalizat la forma standard.",
  "imp.btn": "Import în masă",
  "imp.title": "Import în masă al furnizorilor",
  "imp.desc": "Lipiți date din Excel/Google Sheets sau încărcați un fișier CSV/XLSX. Coloană obligatorie: {name}. Opționale: {list}.",
  "imp.uploadFile": "Încarcă fișier (CSV/XLSX)",
  "imp.downloadTpl": "Descarcă șablon CSV",
  "imp.pasteLabel": "Sau lipiți text CSV / TSV",
  "imp.headerErr.empty": "Fișier gol.",
  "imp.headerErr.noName": "Lipsește coloana „name\" (sau „nume\").",
  "imp.validCnt": "Gata: {n}",
  "imp.invalidCnt": "Cu eroare: {n}",
  "imp.totalRows": "Rânduri totale: {n}",
  "imp.th.row": "Rând",
  "imp.th.name": "Nume",
  "imp.th.categories": "Categorii",
  "imp.th.links": "Linkuri",
  "imp.th.status": "Stare",
  "imp.rowEmpty": "(gol)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Categorii cunoscute: {list}. Categoriile proprii sunt permise (max. 8 per furnizor).",
  "imp.cancel": "Anulează",
  "imp.importBtn": "Importă",
  "imp.err.rowNoName": "Lipsește numele",
  "imp.err.rowNameLong": "Nume mai lung de 120 de caractere",
  "imp.err.invalidUrl": "URL nevalid ({label})",
  "imp.err.badProto": "Protocol nepermis ({label})",
  "imp.err.wrongHost": "Linkul aparține {other}, nu {label}",
  "imp.err.notAllowed": "Domeniul „{host}\" nu este permis ({label})",
  "imp.err.readFile": "Nu s-a putut citi fișierul: {msg}",
  "imp.err.nothing": "Nimic de importat.",
  "imp.toast.imported": "Furnizori importați: {n}",
  "imp.toast.failed": "Import eșuat: {msg}",
  "pub.header": "Furnizori",
  "pub.all": "Toate",
  "det.head.title": "Furnizor — Cerveza.International",
  "det.head.desc": "Detalii furnizor cu linkuri către profilurile sale externe.",
  "det.err.title": "Eroare",
  "det.err.retry": "Încearcă din nou",
  "det.nf.title": "Furnizor negăsit",
  "det.nf.back": "← Înapoi la profilul localului",
  "det.forBusiness": "Furnizor pentru {name}",
  "det.cats.title": "Categorii de aprovizionare",
  "det.note.title": "Notă",
  "det.ext.title": "Profiluri externe",
  "det.ext.empty": "Furnizorul nu a indicat încă niciun profil extern.",
  "det.ext.open": "Deschide",
  "det.allowed": "Domenii permise: {list}.",
  "det.businessFallback": "local",
};

const it: SupplierDict = {
  "title": "Fornitori ({count})",
  "desc.prefix": "Elenco dei fornitori da cui acquistate ingredienti e bevande. Assegnate una o più categorie a ciascun fornitore. I link possono puntare solo a",
  "desc.and": "e",
  "searchPh": "Cerca fornitore…",
  "searchAria": "Cerca fornitore",
  "categoryPh": "Categoria",
  "allCategories": "Tutte le categorie",
  "sortAria": "Ordinamento",
  "sort.default": "Ordine predefinito",
  "sort.name_asc": "Nome (A–Z)",
  "sort.name_desc": "Nome (Z–A)",
  "sort.updated_desc": "Aggiornati di recente (più nuovi)",
  "sort.updated_asc": "Aggiornati di recente (più vecchi)",
  "clearFilters": "Cancella filtri",
  "showing.zero": "Mostrati 0 su {total}",
  "showing.range": "Mostrati {from}–{to} su {count}",
  "showing.filteredSuffix": " (filtrati da {total})",
  "loading": "Caricamento…",
  "empty": "Ancora nessun fornitore.",
  "noMatch": "Nessun fornitore corrisponde ai filtri.",
  "selectAllAria": "Seleziona tutti (secondo filtro)",
  "selected.n": "Selezionati {n}",
  "selected.mixedSuffix": " ({inFiltered} su {shown} visibili)",
  "selected.sameSuffix": " su {shown}",
  "selectAll": "Seleziona tutto ({n})",
  "bulk.addCats": "Aggiungi categorie",
  "bulk.removeCats": "Rimuovi categorie",
  "bulk.deleteConfirm": "Eliminare {n} fornitori selezionati? Azione irreversibile.",
  "bulk.deleteBtn": "Elimina selezionati",
  "bulk.clearSel": "Annulla selezione",
  "row.selectAria": "Seleziona {name}",
  "row.edit": "Modifica",
  "row.delete": "Elimina",
  "row.deleteConfirm": "Eliminare il fornitore «{name}»?",
  "form.title.edit": "Modifica fornitore",
  "form.title.add": "Aggiungi fornitore",
  "form.name": "Nome *",
  "form.namePh": "es. Macelleria Novák",
  "form.categories": "Categorie",
  "form.customCatPh": "Categoria personalizzata…",
  "form.add": "Aggiungi",
  "form.removeAria": "Rimuovi {name}",
  "form.note": "Nota",
  "form.notePh": "opzionale: assortimento, regione, collaborazione…",
  "form.allowedTitle": "Domini consentiti per i link del fornitore",
  "form.allowedSubs": "(e qualsiasi sottodominio {subs})",
  "form.allowedProto": "Richiesto protocollo {https}, senza spazi, max 500 caratteri.",
  "form.link": "Link a {label}",
  "form.onlyAllowed": "— consentito solo",
  "status.valid": "Valido",
  "status.invalid": "Non valido",
  "status.empty": "Vuoto",
  "err.expected": " Atteso il dominio {host} o {subs}.",
  "err.useHttps": " Usa {https}.",
  "err.fixLinks": "Correggi i link non validi: {list}. Domini consentiti: {hosts}.",
  "form.save": "Salva modifiche",
  "form.cancel": "Annulla",
  "form.tipFix": "Correggi i link non validi prima di salvare.",
  "form.tipName": "Inserisci il nome del fornitore.",
  "bulk.selectedCount": "({n} selezionati)",
  "bulk.addDesc": "Le categorie selezionate saranno aggiunte a tutti i fornitori contrassegnati (i duplicati sono ignorati).",
  "bulk.removeDesc": "Le categorie selezionate saranno rimosse da tutti i fornitori contrassegnati.",
  "pg.pageSize": "Per pagina",
  "pg.page": "Pagina {cur} / {total}",
  "v.tooLong": "Link troppo lungo (max 500 caratteri).",
  "v.noSpaces": "Il link non può contenere spazi.",
  "v.invalidUrl": "URL non valido. Inserisci l'indirizzo completo che inizia con https:// (es. https://{host}/…).",
  "v.badProto": "Protocollo «{proto}» non consentito. Usa https://.",
  "v.wrongHost": "Questo link appartiene a {other}. Inseriscilo nel campo «Link a {other}».",
  "v.notAllowed": "Il dominio «{host}» non è consentito. Sono ammessi solo {hosts}.",
  "v.mustPoint": "Il link deve puntare a {label}.",
  "v.nameReq": "Inserisci il nome del fornitore.",
  "v.nameLong": "Nome troppo lungo (max 120 caratteri).",
  "v.noteLong": "Nota troppo lunga (max 500 caratteri).",
  "v.maxCats": "Massimo 8 categorie.",
  "t.editOk": "Fornitore aggiornato.",
  "t.addOk": "Fornitore aggiunto.",
  "t.delOk": "Fornitore eliminato.",
  "t.bulkDelOk": "{n} fornitori eliminati.",
  "t.bulkEditOk": "{n} fornitori aggiornati.",
  "t.linkNorm": "Link normalizzato in forma standard.",
  "imp.btn": "Import in blocco",
  "imp.title": "Import in blocco dei fornitori",
  "imp.desc": "Incolla i dati da Excel/Google Sheets o carica un file CSV/XLSX. Colonna obbligatoria: {name}. Opzionali: {list}.",
  "imp.uploadFile": "Carica file (CSV/XLSX)",
  "imp.downloadTpl": "Scarica modello CSV",
  "imp.pasteLabel": "Oppure incolla testo CSV / TSV",
  "imp.headerErr.empty": "File vuoto.",
  "imp.headerErr.noName": "Manca la colonna «name» (o «nome»).",
  "imp.validCnt": "Pronti: {n}",
  "imp.invalidCnt": "Con errore: {n}",
  "imp.totalRows": "Righe totali: {n}",
  "imp.th.row": "Riga",
  "imp.th.name": "Nome",
  "imp.th.categories": "Categorie",
  "imp.th.links": "Link",
  "imp.th.status": "Stato",
  "imp.rowEmpty": "(vuoto)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Categorie note: {list}. Le categorie personalizzate sono consentite (max 8 per fornitore).",
  "imp.cancel": "Annulla",
  "imp.importBtn": "Importa",
  "imp.err.rowNoName": "Nome mancante",
  "imp.err.rowNameLong": "Nome oltre 120 caratteri",
  "imp.err.invalidUrl": "URL non valido ({label})",
  "imp.err.badProto": "Protocollo non consentito ({label})",
  "imp.err.wrongHost": "Il link appartiene a {other}, non a {label}",
  "imp.err.notAllowed": "Dominio «{host}» non consentito ({label})",
  "imp.err.readFile": "Impossibile leggere il file: {msg}",
  "imp.err.nothing": "Niente da importare.",
  "imp.toast.imported": "Fornitori importati: {n}",
  "imp.toast.failed": "Import non riuscito: {msg}",
  "pub.header": "Fornitori",
  "pub.all": "Tutti",
  "det.head.title": "Fornitore — Cerveza.International",
  "det.head.desc": "Dettaglio del fornitore con link ai profili esterni.",
  "det.err.title": "Errore",
  "det.err.retry": "Riprova",
  "det.nf.title": "Fornitore non trovato",
  "det.nf.back": "← Torna al profilo del locale",
  "det.forBusiness": "Fornitore per {name}",
  "det.cats.title": "Categorie di fornitura",
  "det.note.title": "Nota",
  "det.ext.title": "Profili esterni",
  "det.ext.empty": "Il fornitore non ha ancora indicato alcun profilo esterno.",
  "det.ext.open": "Apri",
  "det.allowed": "Domini consentiti: {list}.",
  "det.businessFallback": "locale",
};

const hu: SupplierDict = {
  "title": "Beszállítók ({count})",
  "desc.prefix": "A beszállítók listája, akiktől alapanyagokat és italokat vásárol. Rendeljen minden beszállítóhoz egy vagy több kategóriát. Linkek csak a következőkre mutathatnak:",
  "desc.and": "és",
  "searchPh": "Beszállító keresése…",
  "searchAria": "Beszállító keresése",
  "categoryPh": "Kategória",
  "allCategories": "Minden kategória",
  "sortAria": "Rendezés",
  "sort.default": "Alapértelmezett sorrend",
  "sort.name_asc": "Név (A–Z)",
  "sort.name_desc": "Név (Z–A)",
  "sort.updated_desc": "Utoljára módosítva (legújabb)",
  "sort.updated_asc": "Utoljára módosítva (legrégebbi)",
  "clearFilters": "Szűrők törlése",
  "showing.zero": "0 megjelenítve / {total}",
  "showing.range": "{from}–{to} megjelenítve / {count}",
  "showing.filteredSuffix": " (szűrve {total}-ból)",
  "loading": "Betöltés…",
  "empty": "Még nincs beszállító.",
  "noMatch": "Nincs a szűrőknek megfelelő beszállító.",
  "selectAllAria": "Összes kijelölése (szűrő szerint)",
  "selected.n": "{n} kijelölve",
  "selected.mixedSuffix": " ({inFiltered} / {shown} látható)",
  "selected.sameSuffix": " / {shown}",
  "selectAll": "Összes kijelölése ({n})",
  "bulk.addCats": "Kategóriák hozzáadása",
  "bulk.removeCats": "Kategóriák eltávolítása",
  "bulk.deleteConfirm": "Töröl {n} kijelölt beszállítót? A művelet nem vonható vissza.",
  "bulk.deleteBtn": "Kijelöltek törlése",
  "bulk.clearSel": "Kijelölés megszüntetése",
  "row.selectAria": "{name} kijelölése",
  "row.edit": "Szerkesztés",
  "row.delete": "Törlés",
  "row.deleteConfirm": "Törli a(z) „{name}\" beszállítót?",
  "form.title.edit": "Beszállító szerkesztése",
  "form.title.add": "Beszállító hozzáadása",
  "form.name": "Név *",
  "form.namePh": "pl. Novák Hentes",
  "form.categories": "Kategóriák",
  "form.customCatPh": "Egyéni kategória…",
  "form.add": "Hozzáadás",
  "form.removeAria": "{name} eltávolítása",
  "form.note": "Megjegyzés",
  "form.notePh": "opcionális: választék, régió, együttműködés…",
  "form.allowedTitle": "Engedélyezett domének a beszállítói linkekhez",
  "form.allowedSubs": "(és bármely aldomén {subs})",
  "form.allowedProto": "Kötelező a {https} protokoll, szóközök nélkül, max. 500 karakter.",
  "form.link": "Link a következőre: {label}",
  "form.onlyAllowed": "— csak",
  "status.valid": "Érvényes",
  "status.invalid": "Érvénytelen",
  "status.empty": "Üres",
  "err.expected": " Elvárt domén: {host} vagy {subs}.",
  "err.useHttps": " Használja: {https}.",
  "err.fixLinks": "Javítsa az érvénytelen linkeket: {list}. Engedélyezett domének: {hosts}.",
  "form.save": "Módosítások mentése",
  "form.cancel": "Mégse",
  "form.tipFix": "Mentés előtt javítsa az érvénytelen linkeket.",
  "form.tipName": "Adja meg a beszállító nevét.",
  "bulk.selectedCount": "({n} kijelölve)",
  "bulk.addDesc": "A kijelölt kategóriák hozzáadódnak minden megjelölt beszállítóhoz (ismétlődések figyelmen kívül).",
  "bulk.removeDesc": "A kijelölt kategóriák eltávolításra kerülnek minden megjelölt beszállítótól.",
  "pg.pageSize": "Oldalanként",
  "pg.page": "{cur} / {total} oldal",
  "v.tooLong": "A link túl hosszú (max. 500 karakter).",
  "v.noSpaces": "A link nem tartalmazhat szóközt.",
  "v.invalidUrl": "Érvénytelen URL. Adjon meg teljes címet https:// kezdéssel (pl. https://{host}/…).",
  "v.badProto": "„{proto}\" protokoll nem engedélyezett. Használjon https://.",
  "v.wrongHost": "Ez a link a(z) {other} címhez tartozik. Tegye a „Link a következőre: {other}\" mezőbe.",
  "v.notAllowed": "„{host}\" domén nem engedélyezett. Csak a következők engedélyezettek: {hosts}.",
  "v.mustPoint": "A linknek a következőre kell mutatnia: {label}.",
  "v.nameReq": "Adja meg a beszállító nevét.",
  "v.nameLong": "A név túl hosszú (max. 120 karakter).",
  "v.noteLong": "A megjegyzés túl hosszú (max. 500 karakter).",
  "v.maxCats": "Legfeljebb 8 kategória.",
  "t.editOk": "Beszállító frissítve.",
  "t.addOk": "Beszállító hozzáadva.",
  "t.delOk": "Beszállító törölve.",
  "t.bulkDelOk": "{n} beszállító törölve.",
  "t.bulkEditOk": "{n} beszállító frissítve.",
  "t.linkNorm": "A link szabványos formára alakítva.",
  "imp.btn": "Tömeges import",
  "imp.title": "Beszállítók tömeges importja",
  "imp.desc": "Illessze be az Excel/Google Sheets adatokat, vagy töltsön fel CSV/XLSX fájlt. Kötelező oszlop: {name}. Opcionális: {list}.",
  "imp.uploadFile": "Fájl feltöltése (CSV/XLSX)",
  "imp.downloadTpl": "CSV-sablon letöltése",
  "imp.pasteLabel": "Vagy illessze be a CSV / TSV szöveget",
  "imp.headerErr.empty": "Üres fájl.",
  "imp.headerErr.noName": "Hiányzik a „name\" (vagy „név\") oszlop.",
  "imp.validCnt": "Kész: {n}",
  "imp.invalidCnt": "Hibás: {n}",
  "imp.totalRows": "Sorok összesen: {n}",
  "imp.th.row": "Sor",
  "imp.th.name": "Név",
  "imp.th.categories": "Kategóriák",
  "imp.th.links": "Linkek",
  "imp.th.status": "Állapot",
  "imp.rowEmpty": "(üres)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Ismert kategóriák: {list}. Egyéni kategóriák engedélyezettek (max. 8 beszállítónként).",
  "imp.cancel": "Mégse",
  "imp.importBtn": "Import",
  "imp.err.rowNoName": "Hiányzó név",
  "imp.err.rowNameLong": "A név 120 karakternél hosszabb",
  "imp.err.invalidUrl": "Érvénytelen URL ({label})",
  "imp.err.badProto": "Nem engedélyezett protokoll ({label})",
  "imp.err.wrongHost": "A link a(z) {other} címhez tartozik, nem a(z) {label} címhez",
  "imp.err.notAllowed": "„{host}\" domén nem engedélyezett ({label})",
  "imp.err.readFile": "A fájl beolvasása sikertelen: {msg}",
  "imp.err.nothing": "Nincs mit importálni.",
  "imp.toast.imported": "Importált beszállítók: {n}",
  "imp.toast.failed": "Az import sikertelen: {msg}",
  "pub.header": "Beszállítók",
  "pub.all": "Mind",
  "det.head.title": "Beszállító — Cerveza.International",
  "det.head.desc": "Beszállító adatlapja külső profiljaira mutató linkekkel.",
  "det.err.title": "Hiba",
  "det.err.retry": "Újrapróbál",
  "det.nf.title": "Nincs ilyen beszállító",
  "det.nf.back": "← Vissza a hely adatlapjára",
  "det.forBusiness": "{name} beszállítója",
  "det.cats.title": "Beszállítási kategóriák",
  "det.note.title": "Megjegyzés",
  "det.ext.title": "Külső profilok",
  "det.ext.empty": "A beszállító még nem adott meg külső profilt.",
  "det.ext.open": "Megnyitás",
  "det.allowed": "Engedélyezett domének: {list}.",
  "det.businessFallback": "hely",
};

const bg: SupplierDict = {
  "title": "Доставчици ({count})",
  "desc.prefix": "Списък с доставчици, от които купувате съставки и напитки. Задайте на всеки доставчик една или повече категории. Връзките могат да сочат само към",
  "desc.and": "и",
  "searchPh": "Търсене на доставчик…",
  "searchAria": "Търсене на доставчик",
  "categoryPh": "Категория",
  "allCategories": "Всички категории",
  "sortAria": "Подреждане",
  "sort.default": "Подразбиран ред",
  "sort.name_asc": "Име (А–Я)",
  "sort.name_desc": "Име (Я–А)",
  "sort.updated_desc": "Наскоро обновени (най-нови)",
  "sort.updated_asc": "Наскоро обновени (най-стари)",
  "clearFilters": "Изчисти филтрите",
  "showing.zero": "Показани 0 от {total}",
  "showing.range": "Показани {from}–{to} от {count}",
  "showing.filteredSuffix": " (филтрирани от {total})",
  "loading": "Зареждане…",
  "empty": "Все още няма доставчици.",
  "noMatch": "Няма доставчик, отговарящ на филтрите.",
  "selectAllAria": "Избери всички (по филтъра)",
  "selected.n": "Избрани {n}",
  "selected.mixedSuffix": " ({inFiltered} от {shown} видими)",
  "selected.sameSuffix": " от {shown}",
  "selectAll": "Избери всички ({n})",
  "bulk.addCats": "Добави категории",
  "bulk.removeCats": "Премахни категории",
  "bulk.deleteConfirm": "Да се изтрият {n} избрани доставчици? Действието е необратимо.",
  "bulk.deleteBtn": "Изтрий избраните",
  "bulk.clearSel": "Изчисти избора",
  "row.selectAria": "Избери {name}",
  "row.edit": "Редакция",
  "row.delete": "Изтриване",
  "row.deleteConfirm": "Да се изтрие доставчик „{name}\"?",
  "form.title.edit": "Редакция на доставчик",
  "form.title.add": "Добавяне на доставчик",
  "form.name": "Име *",
  "form.namePh": "напр. Месарница Новак",
  "form.categories": "Категории",
  "form.customCatPh": "Собствена категория…",
  "form.add": "Добави",
  "form.removeAria": "Премахни {name}",
  "form.note": "Бележка",
  "form.notePh": "по избор: асортимент, регион, сътрудничество…",
  "form.allowedTitle": "Разрешени домейни за връзките на доставчика",
  "form.allowedSubs": "(и произволни поддомейни {subs})",
  "form.allowedProto": "Изисква се протокол {https}, без интервали, макс. 500 знака.",
  "form.link": "Връзка към {label}",
  "form.onlyAllowed": "— разрешен само",
  "status.valid": "Валиден",
  "status.invalid": "Невалиден",
  "status.empty": "Празно",
  "err.expected": " Очакван домейн {host} или {subs}.",
  "err.useHttps": " Използвайте {https}.",
  "err.fixLinks": "Поправете невалидните връзки: {list}. Разрешени домейни: {hosts}.",
  "form.save": "Запази промените",
  "form.cancel": "Отказ",
  "form.tipFix": "Поправете невалидните връзки преди запис.",
  "form.tipName": "Въведете името на доставчика.",
  "bulk.selectedCount": "({n} избрани)",
  "bulk.addDesc": "Избраните категории ще бъдат добавени към всички отбелязани доставчици (дубликатите се игнорират).",
  "bulk.removeDesc": "Избраните категории ще бъдат премахнати от всички отбелязани доставчици.",
  "pg.pageSize": "На страница",
  "pg.page": "Страница {cur} / {total}",
  "v.tooLong": "Връзката е твърде дълга (макс. 500 знака).",
  "v.noSpaces": "Връзката не може да съдържа интервали.",
  "v.invalidUrl": "Невалиден URL. Въведете пълен адрес, започващ с https:// (напр. https://{host}/…).",
  "v.badProto": "Протокол „{proto}\" не е разрешен. Използвайте https://.",
  "v.wrongHost": "Тази връзка принадлежи на {other}. Поставете я в полето „Връзка към {other}\".",
  "v.notAllowed": "Домейнът „{host}\" не е разрешен. Разрешени са само {hosts}.",
  "v.mustPoint": "Връзката трябва да сочи към {label}.",
  "v.nameReq": "Въведете името на доставчика.",
  "v.nameLong": "Името е твърде дълго (макс. 120 знака).",
  "v.noteLong": "Бележката е твърде дълга (макс. 500 знака).",
  "v.maxCats": "Максимум 8 категории.",
  "t.editOk": "Доставчикът е обновен.",
  "t.addOk": "Доставчикът е добавен.",
  "t.delOk": "Доставчикът е изтрит.",
  "t.bulkDelOk": "Изтрити {n} доставчици.",
  "t.bulkEditOk": "Обновени {n} доставчици.",
  "t.linkNorm": "Връзката е нормализирана до стандартен вид.",
  "imp.btn": "Групов внос",
  "imp.title": "Групов внос на доставчици",
  "imp.desc": "Поставете данни от Excel/Google Sheets или качете CSV/XLSX файл. Задължителна колона: {name}. По избор: {list}.",
  "imp.uploadFile": "Качване на файл (CSV/XLSX)",
  "imp.downloadTpl": "Изтегли CSV шаблон",
  "imp.pasteLabel": "Или поставете CSV / TSV текст",
  "imp.headerErr.empty": "Празен файл.",
  "imp.headerErr.noName": "Липсва колона „name\" (или „име\").",
  "imp.validCnt": "Готови: {n}",
  "imp.invalidCnt": "С грешка: {n}",
  "imp.totalRows": "Общо редове: {n}",
  "imp.th.row": "Ред",
  "imp.th.name": "Име",
  "imp.th.categories": "Категории",
  "imp.th.links": "Връзки",
  "imp.th.status": "Статус",
  "imp.rowEmpty": "(празно)",
  "imp.statusOk": "OK",
  "imp.knownCats": "Известни категории: {list}. Собствените категории са позволени (макс. 8 на доставчик).",
  "imp.cancel": "Отказ",
  "imp.importBtn": "Внос",
  "imp.err.rowNoName": "Липсва име",
  "imp.err.rowNameLong": "Име с повече от 120 знака",
  "imp.err.invalidUrl": "Невалиден URL ({label})",
  "imp.err.badProto": "Непозволен протокол ({label})",
  "imp.err.wrongHost": "Връзката принадлежи на {other}, а не на {label}",
  "imp.err.notAllowed": "Домейнът „{host}\" не е позволен ({label})",
  "imp.err.readFile": "Файлът не може да бъде прочетен: {msg}",
  "imp.err.nothing": "Няма какво да се внесе.",
  "imp.toast.imported": "Внесени доставчици: {n}",
  "imp.toast.failed": "Вносът е неуспешен: {msg}",
  "pub.header": "Доставчици",
  "pub.all": "Всички",
  "det.head.title": "Доставчик — Cerveza.International",
  "det.head.desc": "Детайли за доставчика с връзки към външните му профили.",
  "det.err.title": "Грешка",
  "det.err.retry": "Опитай отново",
  "det.nf.title": "Доставчикът не е намерен",
  "det.nf.back": "← Обратно към профила на обекта",
  "det.forBusiness": "Доставчик за {name}",
  "det.cats.title": "Категории доставки",
  "det.note.title": "Бележка",
  "det.ext.title": "Външни профили",
  "det.ext.empty": "Доставчикът все още не е посочил външен профил.",
  "det.ext.open": "Отвори",
  "det.allowed": "Разрешени домейни: {list}.",
  "det.businessFallback": "обект",
};

const DICTS: Record<string, SupplierDict> = {
  cs, sk, pl, en, es, de, pt, fr, hr, ro, it, hu, bg,
};

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

export function supplierText(lang: string, key: string, vars?: Record<string, string | number>): string {
  const dict = DICTS[lang] ?? DICTS.en;
  const v = dict[key] ?? DICTS.en[key] ?? DICTS.cs[key];
  if (v === undefined) {
    if (typeof console !== "undefined") console.warn(`[suppliers-i18n] missing key "${key}" for lang "${lang}"`);
    return key;
  }
  return interpolate(v, vars);
}

/** React hook returning a translator bound to current UI language. */
export function useSupplierT() {
  const { lang } = useT();
  return (key: string, vars?: Record<string, string | number>) => supplierText(lang, key, vars);
}

export const SUPPLIER_LANGS = Object.keys(DICTS);
